<template>
  <ProfileLayout>
    <template #page-header>
      <span>{{ pageData.title }}</span>
    </template>
    <div class="max-w-[350px] mt-12">
      <form @submit="onSubmit">
        <div
          class="ui-text-m text-primary mb-8"
          v-html="pageData.description"
        />
        <div class="space-y-2">
          <UiInput
            v-model="form.number"
            class="flex-1"
            name="number"
            floating-label
            :label="$t('orderNumber')"
            :placeholder="$t('orderNumber')"
            :lazy="false"
          />
          <UiInput
            v-model="form.email"
            class="flex-1"
            name="email"
            floating-label
            :label="$t('email2')"
            :placeholder="$t('email2')"
            :lazy="false"
          />
          <UiInput
            v-model="form.billing_zip"
            class="flex-1"
            name="billing_zip"
            floating-label
            :label="$t('zipCode')"
            :placeholder="$t('zipCode')"
            :lazy="false"
          />
        </div>
        <div class="mt-4">
          <UiButton
            :loading="isSubmitting"
            :disabled="!meta.valid"
            type="submit"
            color="primary"
            class="w-full"
          >
            {{ $t("trackOrder") }}
          </UiButton>
        </div>
      </form>
    </div>
  </ProfileLayout>
</template>

<script setup lang="ts">
import { object, string } from "yup";
import { useForm } from "vee-validate";
import { FetchError } from "ofetch";

definePageMeta({
  middleware: ["guest-order-tracking"],
});

const { $api } = useNuxtApp();
const router = useRouter();
const { t } = useI18n();
const localePath = useLocalePathPolyfill();
const { openSystemDialog } = useSystemDialogs();
const generalStore = useGeneralStore();
const { visitEvent } = useEvents();

interface InitialForms {
  email: string;
  number: string;
  billing_zip: string;
}

const { data } = useAsyncData("track-order", () => {
  return $api.pages.getPage("track-order", {
    skipErrors: [404, 500],
  });
});

const pageData = computed(() => {
  const currentTranslation = data.value?.data?.currentTranslation;
  return {
    title: currentTranslation?.title || t("orderTracking"),
    description:
      currentTranslation?.description ||
      t("checkOnAnIndividualOrderEvenIfYouChecked"),
    is_plp: data.value?.data?.is_plp,
  };
});

const initialForm: InitialForms = {
  email: "",
  number: "",
  billing_zip: "",
};

const form = reactive(JSON.parse(JSON.stringify(initialForm))) as InitialForms;

const schema = object().shape({
  email: string().email().required().label(t("emailAddress")),
  number: string().required().label(t("orderNumber")),
  billing_zip: string().required().max(10).label(t("billingZipCode")),
});

const { handleSubmit, setErrors, meta, isSubmitting } = useForm({
  initialValues: initialForm,
  validationSchema: schema,
});

const onSubmit = handleSubmit(async (values) => {
  try {
    const data = await $api.orders.getTrackingOrder(values);
    generalStore.setGuestOrderInfo(data?.data);
    await router.push(
      localePath(
        `/orders?email=${values.email}&number=${values.number}&billing_zip=${values.billing_zip}`,
      ),
    );
  } catch (error) {
    if (error instanceof FetchError) {
      setErrors(responseErrorToObjectErrors(error?.data?.errors));

      openSystemDialog({
        type: "alert",
        title: `${t("error")}!`,
        text: error?.data?.errors?.[0]?.message || "Wrong data",
      });
    }
  }
});

onMounted(() => {
  visitEvent(pageData.value?.is_plp);
});
</script>
